import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Image, Plus } from 'lucide-react';
import { useAdsService } from '@/hooks/useAdsService';
import { toast } from 'react-hot-toast';

const CreateAdModal = ({ isOpen, onClose, onCreated }) => {
  const { createAd, loading, error } = useAdsService();
  
  // Form state
  const [title, setTitle] = useState('');
  const [isActive, setIsActive] = useState(true);
  
  // Image state
  const [landscapeImage, setLandscapeImage] = useState(null);
  const [portraitImage, setPortraitImage] = useState(null);
  const [landscapePreview, setLandscapePreview] = useState(null);
  const [portraitPreview, setPortraitPreview] = useState(null);
  


  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setIsActive(true);
      setLandscapeImage(null);
      setPortraitImage(null);
      setLandscapePreview(null);
      setPortraitPreview(null);
    }
  }, [isOpen]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Handle image selection
  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'landscape') {
        setLandscapeImage(file);
        setLandscapePreview(URL.createObjectURL(file));
      } else {
        setPortraitImage(file);
        setPortraitPreview(URL.createObjectURL(file));
      }
    }
  };

  // Convert file to base64 data URL
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };



  // Handle form submit
  const handleCreate = async () => {
    if (!title.trim()) {
      toast.error('Please enter an ad title');
      return;
    }
    
    if (!landscapeImage) {
      toast.error('Please select a landscape image');
      return;
    }
    
    if (!portraitImage) {
      toast.error('Please select a portrait image');
      return;
    }

    try {
      // Convert images to base64
      const landscapeDataUrl = await fileToBase64(landscapeImage);
      const portraitDataUrl = await fileToBase64(portraitImage);
      
      // Prepare ad data
      const adData = {
        title: title.trim(),
        active: isActive
      };
      
      // Create the ad
      const createdAd = await createAd(adData, landscapeDataUrl, portraitDataUrl);
      
      toast.success('Ad created successfully!');
      onCreated?.(createdAd);
      onClose();
    } catch (err) {
      console.error('Error creating ad:', err);
      toast.error(`Failed to create ad: ${err.message}`);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 overflow-hidden touch-none pointer-events-auto">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl border border-gray-200 shadow-2xl w-full max-w-4xl overflow-hidden overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Image className="w-6 h-6 text-blue-500" />
            <span className="font-bold text-xl text-gray-900">Create New Advertisement</span>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Top Section - Title and Active Checkbox */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ad Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter ad title"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={loading}
              />
            </div>



            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={loading}
              />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                Active (visible to users)
              </label>
            </div>
          </div>

          {/* Bottom Section - Image Uploads Side by Side */}
          <div className="flex gap-6">
                        {/* Landscape Image */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Landscape Image * (16:9)
              </label>
              {landscapePreview ? (
                <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
                  <img
                    src={landscapePreview}
                    alt="Landscape preview"
                    className="w-full h-full object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('landscape-upload').click()}
                    className="absolute bottom-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors text-center cursor-pointer flex flex-col items-center justify-center"
                  style={{ aspectRatio: '16/9' }}
                  onClick={() => document.getElementById('landscape-upload').click()}
                >
                  <Upload className="w-8 h-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">Click to upload landscape image</p>
                </div>
              )}
              <input
                id="landscape-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, 'landscape')}
                className="hidden"
                disabled={loading}
              />
            </div>

            {/* Portrait Image */}
            <div className="flex-1 flex justify-center">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portrait Image * (9:16)
                </label>
              <div className="relative" style={{ width: '200px', aspectRatio: '9/16' }}>
                {portraitPreview ? (
                  <>
                    <img
                      src={portraitPreview}
                      alt="Portrait preview"
                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => document.getElementById('portrait-upload').click()}
                      className="absolute bottom-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    >
                      Change
                    </button>
                  </>
                ) : (
                  <div 
                    className="w-full h-full border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors text-center cursor-pointer flex flex-col items-center justify-center"
                    onClick={() => document.getElementById('portrait-upload').click()}
                  >
                    <Upload className="w-8 h-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">Click to upload portrait image</p>
                  </div>
                )}
              </div>
              <input
                id="portrait-upload"
                type="file"
                accept="image/*"
                onChange={(e) => handleImageChange(e, 'portrait')}
                className="hidden"
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={loading || !title.trim() || !landscapeImage || !portraitImage}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Create Ad
            </>
          )}
        </button>
      </div>
    </div>
  </div>,
  document.body
);
};

export default CreateAdModal; 